import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false
}: PaginationProps) => {
  
  const handlePrevious = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    const validTotalPages = Math.max(1, totalPages);
    if (currentPage < validTotalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage && !isLoading && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    const validTotalPages = Math.max(1, totalPages);
    
    if (validTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= validTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(validTotalPages);
      } else if (currentPage >= validTotalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = validTotalPages - 2; i <= validTotalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(validTotalPages);
      }
    }
    
    return pages;
  };

  const baseButtonClass = `
    w-10 h-10 rounded-full border-2 border-[#1BCF66] 
    flex items-center justify-center transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const pageButtonClass = `
    w-10 h-10 rounded-full border-2 border-[#1BCF66] 
    flex items-center justify-center transition-all duration-200
    hover:bg-[#1B2C23] cursor-pointer
  `;

  const activePageClass = `
    bg-[#1B2C23] text-[#1BCF66] font-medium
  `;

  const inactivePageClass = `
    bg-transparent text-[#1BCF66] font-medium
  `;

  const navigationButtonClass = `
    bg-transparent text-[#1BCF66] hover:bg-[#1B2C23]
  `;

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={handlePrevious}
        disabled={currentPage <= 1 || isLoading}
        className={`${baseButtonClass} ${navigationButtonClass}`}
      >
        <ChevronLeft size={16} />
      </button>

      {renderPageNumbers().map((page, index) => (
        <React.Fragment key={index}>
          {page === '...' ? (
            <span className="text-[#1BCF66] px-2">...</span>
          ) : (
            <button
              onClick={() => handlePageClick(page as number)}
              disabled={isLoading}
              className={`${pageButtonClass} ${
                page === currentPage ? activePageClass : inactivePageClass
              }`}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      <button
        onClick={handleNext}
        disabled={currentPage >= Math.max(1, totalPages) || isLoading}
        className={`${baseButtonClass} ${navigationButtonClass}`}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

export default Pagination;
